import { get, post } from '@/lib/api-client'
import type {
  AuthUser,
  LoginRequest,
  LoginResponse,
  OAuthProvider,
  OAuthRedirectResponse,
  TokenRefreshRequest,
  TokenRefreshResponse,
} from '@/types/auth'

/**
 * Authentication API endpoints
 * Provides methods for login, logout, and token management
 */
export const authApi = {
  /**
   * Login with email and password
   */
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    return post<LoginResponse, LoginRequest>('/auth/login', data)
  },

  /**
   * Logout the current user
   */
  logout: async (): Promise<void> => {
    return post<void>('/auth/logout')
  },

  /**
   * Refresh access token using refresh token
   */
  refreshToken: async (data: TokenRefreshRequest): Promise<TokenRefreshResponse> => {
    return post<TokenRefreshResponse, TokenRefreshRequest>('/auth/refresh', data)
  },

  /**
   * Get OAuth redirect URL for a specific provider
   * Note: only Okta is wired up — handled by direct browser redirect to /api/auth/sso/okta/login.
   */
  getOAuthRedirectUrl: async (provider: OAuthProvider): Promise<OAuthRedirectResponse> => {
    if (provider === 'okta') {
      return { redirectUrl: '/api/auth/sso/okta/login', state: '' }
    }
    throw new Error(`OAuth provider ${provider} is not yet implemented`)
  },

  /**
   * Fetch the currently-authenticated user via /api/auth/me.
   */
  getCurrentUser: async (): Promise<AuthUser> => {
    return get<AuthUser>('/auth/me')
  },
}

/**
 * Generate a random state parameter for CSRF protection
 */
export function generateOAuthState(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * Store OAuth state in session storage for verification
 */
export function storeOAuthState(state: string): void {
  sessionStorage.setItem('oauth_state', state)
}

/**
 * Verify OAuth state parameter matches stored value
 */
export function verifyOAuthState(state: string): boolean {
  const storedState = sessionStorage.getItem('oauth_state')
  sessionStorage.removeItem('oauth_state')
  return storedState === state
}

/**
 * Store intended destination for post-login redirect
 */
export function storeIntendedDestination(path: string): void {
  sessionStorage.setItem('intended_destination', path)
}

/**
 * Get and clear intended destination
 */
export function getIntendedDestination(): string | null {
  const destination = sessionStorage.getItem('intended_destination')
  sessionStorage.removeItem('intended_destination')
  return destination
}
